import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React from 'react';
import { FunnelCartDelivery } from 'src/ts/types/cart';
import { getLocalTimezoneDisplayDataFromDateString } from 'src/utils/dateFormatter';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import Image from 'next/image';
import AddRemoveSummaryItems from './AddRemoveSummaryItems';
import { inDollars } from 'src/utils/currency';

interface Props {
  cartDelivery: FunnelCartDelivery;
  expanded: boolean;
  onToggle: () => void;
  addMealToCart: (productVariantId: number, cartDeliveryId: number) => void;
  removeMealFromCart: (
    productVariantId: number,
    cartDeliveryId: number,
  ) => void;
  totalDeliveries: number;
  displayError?: string;
  totalDisplayErrors: number;
  soldOutVariantIds: number[];
}

const MenuMealsSummary: React.FC<Props> = ({
  cartDelivery,
  expanded,
  onToggle,
  addMealToCart,
  removeMealFromCart,
  totalDeliveries,
  displayError,
  totalDisplayErrors,
  soldOutVariantIds,
}) => {
  const theme = useTheme();

  // This nonsense math is to indicate to the content of the expanded Accordian how much space can
  // be taken up. Since this whole drawer needs to take into account the number of errors shown and
  // multiple delivery days, this math handles that.
  //
  // There may be a better way to do this in smarter CSS, but I couldn't figure it out.
  const HEADER_HEIGHT = 54;
  const GAP = 8;
  const ALERT_HEIGHT = 80;
  const nonContentHeight =
    totalDeliveries * HEADER_HEIGHT +
    (totalDeliveries - 1) * GAP +
    totalDisplayErrors * ALERT_HEIGHT;

  const hasItems =
    cartDelivery.lineItems.reduce((acc, lineItem) => {
      return acc + lineItem.quantity;
    }, 0) > 0;

  return (
    <>
      {displayError && (
        <Alert variant="outlined" severity="error">
          {displayError}
        </Alert>
      )}
      <Accordion
        expanded={expanded}
        onChange={onToggle}
        elevation={0}
        // disableGutters removes the extra margin and header height increase MUI adds when expanded.
        disableGutters
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: '10px !important',
          // MUI adds a border-top divider between adjacent Accordions via a ::before pseudo-element.
          // This removes it since we're using our own border styling.
          '&::before': { display: 'none' },
        }}
      >
        <AccordionSummary disabled={!hasItems} expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* This icon is getting hidden on very small screens to prevent text wrapping */}
            <LocalShippingOutlinedIcon
              sx={{
                color: theme.palette.grey[700],
                display: { xs: 'none', sm: 'flex' },
              }}
            />
            <Typography
              variant="subtitle1"
              sx={{ color: theme.palette.grey[700] }}
              noWrap
            >
              {formatHeaderTextPlainText(cartDelivery)}
              <span
                style={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                }}
              >
                {formatHeaderTextBoldText(cartDelivery)}
              </span>
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            maxHeight: `calc(100cqh - ${nonContentHeight}px)`,
            overflowY: 'auto',
            py: 0,
          }}
        >
          {cartDelivery.lineItems.map((lineItem) => {
            return (
              <Box
                key={`${cartDelivery.id}-${lineItem.variantId}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  mb: 1,
                  gap: 1,
                }}
              >
                <Image
                  src={lineItem.variant.product.imageUrl}
                  alt={lineItem.variant.product.name}
                  height={110}
                  width={110}
                  style={{ objectFit: 'cover', borderRadius: 10 }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minWidth: 0,
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" noWrap>
                      {lineItem.variant.product.title}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: theme.palette.grey[700] }}
                      noWrap
                    >
                      {lineItem.variant.product.subtitle}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: theme.palette.grey[700] }}
                    >
                      ${inDollars(lineItem.price)}
                    </Typography>
                  </Box>
                  <AddRemoveSummaryItems
                    quantity={lineItem.quantity}
                    onAdd={() =>
                      addMealToCart(lineItem.variantId, cartDelivery.id)
                    }
                    onRemove={() =>
                      removeMealFromCart(lineItem.variantId, cartDelivery.id)
                    }
                    isSoldOut={soldOutVariantIds.includes(lineItem.variantId)}
                  />
                </Box>
              </Box>
            );
          })}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

function formatHeaderTextPlainText(cartDelivery: FunnelCartDelivery) {
  const totalItems = cartDelivery.lineItems.reduce((acc, lineItem) => {
    return acc + lineItem.quantity;
  }, 0);

  return `${totalItems} meals delivered on `;
}

function formatHeaderTextBoldText(cartDelivery: FunnelCartDelivery) {
  const { month, day, dayOfWeek } = getLocalTimezoneDisplayDataFromDateString({
    date: cartDelivery.deliveryDate,
    format: 'short',
  });

  return `${dayOfWeek}, ${month} ${day}`;
}

export default MenuMealsSummary;
